import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Bell, Calendar, Trophy, DollarSign, Plus, LogOut, User, Shield, Users, MapPin, Clock, History, ChevronRight, CreditCard, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [paymentChannels, setPaymentChannels] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [players, setPlayers] = useState([]);
  const [attendees, setAttendees] = useState({});
  const [lightboxBanner, setLightboxBanner] = useState(null);
  const [balance, setBalance] = useState({ total_unpaid: 0 });
  const [balanceDetails, setBalanceDetails] = useState({ total: 0, items: [] });
  const [history, setHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showTournamentReg, setShowTournamentReg] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [checkInData, setCheckInData] = useState({ player_id: '', file: null });
  const [newPlayer, setNewPlayer] = useState({ name: '', birthdate: '' });
  const [tournamentRegData, setTournamentRegData] = useState({ player_id: '', file: null });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [announcementsRes, sessionsRes, tournamentsRes, upcomingRes, playersRes, balanceRes, notificationsRes, paymentChannelsRes] = await Promise.all([
        axios.get(`${API}/members/announcements`, { withCredentials: true }),
        axios.get(`${API}/members/training-sessions`, { withCredentials: true }),
        axios.get(`${API}/members/tournaments`, { withCredentials: true }),
        axios.get(`${API}/members/upcoming`, { withCredentials: true }),
        axios.get(`${API}/members/players`, { withCredentials: true }),
        axios.get(`${API}/members/balance`, { withCredentials: true }),
        axios.get(`${API}/members/notifications`, { withCredentials: true }),
        axios.get(`${API}/members/payment-channels`, { withCredentials: true }),
      ]);
      setAnnouncements(announcementsRes.data);
      setTrainingSessions(sessionsRes.data);
      setTournaments(tournamentsRes.data);
      setUpcoming(upcomingRes.data);
      setPlayers(playersRes.data);
      setBalance(balanceRes.data);
      setNotifications(notificationsRes.data);
      setPaymentChannels(paymentChannelsRes.data);

      // Fetch attendees for each session and tournament in parallel
      const allEventIds = [
        ...sessionsRes.data.map(s => ({ type: 'training', id: s.id })),
        ...tournamentsRes.data.map(t => ({ type: 'tournament', id: t.id }))
      ];
      const attendeeResults = await Promise.all(
        allEventIds.map(e =>
          axios.get(
            `${API}/members/${e.type === 'training' ? 'training-sessions' : 'tournaments'}/${e.id}/attendees`,
            { withCredentials: true }
          ).then(r => ({ id: e.id, data: r.data })).catch(() => ({ id: e.id, data: { player_names: [], count: 0 } }))
        )
      );
      const attendeeMap = {};
      attendeeResults.forEach(r => { attendeeMap[r.id] = r.data; });
      setAttendees(attendeeMap);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const unreadNotificationsList = useMemo(
    () => notifications.filter(n => !n.is_read).slice(0, 3),
    [notifications]
  );

  const openBalanceDetails = async () => {
    try {
      const res = await axios.get(`${API}/members/balance-details`, { withCredentials: true });
      setBalanceDetails(res.data);
      setShowBalanceModal(true);
    } catch (error) {
      toast.error('Failed to load balance details');
    }
  };

  const openHistory = async () => {
    try {
      const res = await axios.get(`${API}/members/history`, { withCredentials: true });
      setHistory(res.data);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/members/players`, newPlayer, { withCredentials: true });
      toast.success('Player added successfully!');
      setShowAddPlayer(false);
      setNewPlayer({ name: '', birthdate: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    } finally {
      setLoading(false);
    }
  };

  const openCheckIn = (session) => {
    setSelectedSession(session);
    setCheckInData({ player_id: '', file: null });
    setShowCheckIn(true);
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!checkInData.player_id) {
      toast.error('Please select a player');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('player_id', checkInData.player_id);
      formData.append('training_session_id', selectedSession.id);
      if (checkInData.file) {
        formData.append('file', checkInData.file);
      }
      
      await axios.post(`${API}/members/check-in`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Check-in successful!');
      setShowCheckIn(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setLoading(false);
    }
  };

  const openTournamentRegistration = (tournament) => {
    setSelectedTournament(tournament);
    setTournamentRegData({ player_id: '', file: null });
    setShowTournamentReg(true);
  };

  const handleTournamentRegistration = async (e) => {
    e.preventDefault();
    if (!tournamentRegData.player_id) {
      toast.error('Please select a player');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      if (tournamentRegData.file) {
        formData.append('file', tournamentRegData.file);
      }
      
      await axios.post(
        `${API}/members/tournaments/register?player_id=${tournamentRegData.player_id}&tournament_id=${selectedTournament.id}`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success(tournamentRegData.file ? 'Tournament registration submitted!' : 'Registered! Upload your GCash receipt anytime from Unpaid Items.');
      setShowTournamentReg(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadReceipt = async (item, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = item.type === 'tournament'
        ? `${API}/members/tournament-registrations/${item.id}/upload-receipt`
        : `${API}/members/check-ins/${item.id}/upload-receipt`;
      await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Receipt uploaded!');
      // Refresh balance details
      const res = await axios.get(`${API}/members/balance-details`, { withCredentials: true });
      setBalanceDetails(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload receipt');
    }
  };

  const unreadNotifications = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-primary text-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_family-athletics/artifacts/uy0gl72p_New%20Offcial%20Logo.jpg"
              alt="Dolphins FC"
              className="h-12"
            />
            <div>
              <h1 className="font-barlow-condensed text-2xl font-bold tracking-tight" data-testid="member-dashboard-title">Dolphins FC</h1>
              <p className="text-sm text-white/80">PCU Elementary</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="notifications-button">
                <Bell className="h-5 w-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {unreadNotifications}
                  </span>
                )}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5" />
              <span className="text-sm font-medium">{user?.name}</span>
            </div>
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-white hover:bg-white/10"
                data-testid="back-to-admin-button"
              >
                <Shield className="h-4 w-4 mr-2" />
                Back to Admin
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-white hover:bg-white/10"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Prominent Total Balance Card at Top */}
        <button
          onClick={openBalanceDetails}
          className="w-full mb-8 group"
          data-testid="total-balance-card"
        >
          <Card className="bg-gradient-to-r from-primary via-primary to-secondary p-8 text-white rounded-2xl shadow-lg border-0 hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1 cursor-pointer">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                  <DollarSign className="h-8 w-8" />
                </div>
                <div className="text-left">
                  <p className="text-sm text-white/80 mb-1 uppercase tracking-wider">Total Family Balance</p>
                  <p className="font-barlow-condensed text-5xl font-bold" data-testid="total-balance">₱{balance.total_unpaid}</p>
                  <p className="text-sm text-white/70 mt-1">
                    {balance.check_ins_count || 0} Training · {balance.tournaments_count || 0} Tournament{balance.tournaments_count === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white/90 group-hover:text-accent transition-colors">
                <span className="text-sm font-semibold">View Details</span>
                <ChevronRight className="h-5 w-5" />
              </div>
            </div>
          </Card>
        </button>

        {/* Payment Channels Card - directly below Total Balance */}
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm mb-8 overflow-hidden" data-testid="payment-channels-card">
          <div className="bg-slate-900 text-white px-6 py-4 flex items-center gap-3">
            <CreditCard className="h-6 w-6 text-accent" />
            <div>
              <h2 className="font-barlow-condensed text-xl font-bold tracking-tight">Payment Channels</h2>
              <p className="text-xs text-white/70">Send payments to any channel below, then upload your receipt</p>
            </div>
          </div>
          <div className="p-6">
            {paymentChannels.length === 0 ? (
              <p className="text-slate-500 text-sm">No payment channels configured. Contact admin.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paymentChannels.map(channel => (
                  <div key={channel.id} className="border border-slate-200 rounded-lg p-4 hover:border-secondary transition-colors" data-testid={`payment-channel-${channel.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-barlow-condensed text-lg font-bold text-primary">{channel.name}</h3>
                      <span className="bg-secondary/10 text-secondary px-2 py-0.5 rounded-full text-xs font-semibold">Active</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Account Name:</span> <span className="font-semibold text-primary">{channel.account_name}</span></p>
                      <p><span className="text-slate-500">Number:</span> <span className="font-mono font-semibold text-primary">{channel.number}</span></p>
                      {channel.instructions && (
                        <p className="text-xs text-slate-600 mt-2 pt-2 border-t border-slate-100">{channel.instructions}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {unreadNotifications > 0 && (
          <div className="bg-secondary/10 border border-secondary rounded-lg p-4 mb-6" data-testid="notifications-banner">
            <div className="flex items-start gap-3">
              <Bell className="h-5 w-5 text-secondary mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-primary mb-2">New Notifications</h3>
                {unreadNotificationsList.map(notif => (
                  <p key={notif.id} className="text-sm text-slate-600 mb-1">{notif.message}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* My Players */}
            <Card className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="players-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-barlow-condensed text-xl font-bold text-primary tracking-tight">My Players</h2>
                <Dialog open={showAddPlayer} onOpenChange={setShowAddPlayer}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-secondary hover:bg-secondary/90 text-white" data-testid="add-player-button">
                      <Plus className="h-4 w-4 mr-1" /> Add Child
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Player</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddPlayer} className="space-y-4">
                      <div>
                        <Label>Child&apos;s Name</Label>
                        <Input value={newPlayer.name} onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })} required data-testid="add-player-name-input" />
                      </div>
                      <div>
                        <Label>Birthdate</Label>
                        <Input type="date" value={newPlayer.birthdate} onChange={(e) => setNewPlayer({ ...newPlayer, birthdate: e.target.value })} required data-testid="add-player-birthdate-input" max={new Date().toISOString().split('T')[0]} />
                        {newPlayer.birthdate && (() => {
                          const bd = new Date(newPlayer.birthdate);
                          const today = new Date();
                          let age = today.getFullYear() - bd.getFullYear();
                          const m = today.getMonth() - bd.getMonth();
                          if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
                          return <p className="text-sm text-slate-600 mt-2">Current Age: <span className="font-semibold text-primary">{age} years</span> · Category: <span className="font-semibold text-secondary">U{age}</span></p>;
                        })()}
                      </div>
                      <Button type="submit" disabled={loading} className="w-full bg-primary text-white hover:bg-primary/90" data-testid="add-player-submit-button">
                        {loading ? 'Adding...' : 'Add Player'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {players.length === 0 ? (
                <p className="text-slate-500 text-sm">No players added yet. Click &quot;Add Child&quot; to get started.</p>
              ) : (
                <div className="space-y-3">
                  {players.map(player => {
                    const status = player.membership_status || 'Non-Member';
                    const isActive = status !== 'Non-Member';
                    return (
                      <div key={player.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 gap-3" data-testid={`player-${player.id}`}>
                        <div className="min-w-0">
                          <p className="font-semibold text-primary truncate">{player.name}</p>
                          <p className="text-sm text-slate-600">
                            {player.age !== undefined ? `Age ${player.age}` : ''}
                            {player.category ? ` • ${player.category}` : ''}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                          data-testid={`player-membership-${player.id}`}
                        >
                          {status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* History Button */}
            <Button
              onClick={openHistory}
              variant="outline"
              className="w-full border-primary text-primary hover:bg-primary hover:text-white py-6"
              data-testid="view-history-button"
            >
              <History className="h-5 w-5 mr-2" />
              View Past Activity
            </Button>

            {/* Announcements */}
            <Card className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="announcements-card">
              <h2 className="font-barlow-condensed text-xl font-bold text-primary tracking-tight mb-4">Announcements</h2>
              {announcements.length === 0 ? (
                <p className="text-slate-500 text-sm">No announcements yet.</p>
              ) : (
                <div className="space-y-4">
                  {announcements.slice(0, 3).map(announcement => (
                    <div key={announcement.id} className="border-l-4 border-secondary pl-4 py-2" data-testid={`announcement-${announcement.id}`}>
                      <h3 className="font-semibold text-primary mb-1">{announcement.title}</h3>
                      <p className="text-sm text-slate-600">{announcement.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming 7-Day Feed */}
            <Card className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="upcoming-feed-card">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="h-6 w-6 text-secondary" />
                <h2 className="font-barlow-condensed text-2xl font-bold text-primary tracking-tight">My Upcoming Schedule</h2>
                <span className="text-xs bg-secondary/20 text-secondary px-2 py-1 rounded-full font-semibold ml-2">All upcoming</span>
              </div>
              {upcoming.length === 0 ? (
                <p className="text-slate-500 text-sm">You haven&apos;t checked into any trainings or registered for tournaments yet. Check the schedule below and sign up!</p>
              ) : (
                <div className="space-y-3">
                  {upcoming.map(event => (
                    <div key={event.id} className={`border-l-4 ${event.type === 'training' ? 'border-secondary' : 'border-accent'} bg-slate-50 rounded-r-lg p-4`} data-testid={`upcoming-${event.id}`}>
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${event.type === 'training' ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'}`}>
                              {event.type === 'training' ? 'Training' : 'Tournament'}
                            </span>
                            {event.category && (
                              <span className="text-xs text-slate-600 font-medium">{event.category}</span>
                            )}
                          </div>
                          <h3 className="font-barlow-condensed text-lg font-bold text-primary">{event.title}</h3>
                          {event.player_names && event.player_names.length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              For: <span className="font-semibold text-primary">{event.player_names.join(', ')}</span>
                            </p>
                          )}
                          <div className="mt-2 space-y-1 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-slate-400" />
                              <span>{event.location}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              <span>{formatDate(event.date_time)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-slate-400" />
                              <span>{formatTime(event.date_time)}</span>
                            </div>
                          </div>
                          {event.notes && (
                            <div className="mt-3 p-2 bg-accent/10 border-l-2 border-accent rounded">
                              <p className="text-xs font-semibold text-accent uppercase tracking-wide">Notes</p>
                              <p className="text-sm text-slate-700">{event.notes}</p>
                            </div>
                          )}
                        </div>
                        <span className="text-sm font-bold text-primary">₱{event.fee}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Training Schedule */}
            <Card className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="training-schedule-card">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="h-6 w-6 text-secondary" />
                <h2 className="font-barlow-condensed text-2xl font-bold text-primary tracking-tight">All Training Sessions</h2>
              </div>
              {trainingSessions.length === 0 ? (
                <p className="text-slate-500 text-sm">No upcoming training sessions.</p>
              ) : (
                <div className="space-y-4">
                  {trainingSessions.map(session => (
                    <div key={session.id} className="border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow" data-testid={`training-session-${session.id}`}>
                      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                        <div className="flex-1">
                          <h3 className="font-barlow-condensed text-lg font-bold text-primary">{session.title}</h3>
                          <div className="mt-2 space-y-1 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-slate-400" />
                              <span>{session.location}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              <span>{formatDate(session.date_time)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-slate-400" />
                              <span>{formatTime(session.date_time)}</span>
                            </div>
                          </div>
                          {session.notes && (
                            <div className="mt-3 p-2 bg-accent/10 border-l-2 border-accent rounded">
                              <p className="text-xs font-semibold text-accent uppercase tracking-wide">Notes</p>
                              <p className="text-sm text-slate-700">{session.notes}</p>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            session.fee_type === 'standard' ? 'bg-green-100 text-green-700' : 'bg-accent/20 text-accent'
                          }`}>
                            {session.fee_type === 'standard' ? '₱100 Standard' : '₱250 Special'}
                          </span>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => openCheckIn(session)} className="bg-accent hover:bg-accent/90 text-white w-full sm:w-auto" data-testid={`check-in-button-${session.id}`}>
                        Check-in Child
                      </Button>
                      {attendees[session.id] && attendees[session.id].count > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                            Confirmed Players ({attendees[session.id].count})
                          </p>
                          <div className="flex flex-wrap gap-1.5" data-testid={`session-attendees-${session.id}`}>
                            {attendees[session.id].player_names.map((name, idx) => (
                              <span key={`${session.id}-${name}-${idx}`} className="inline-block px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full font-medium">
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Tournament Hub */}
            <Card className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="tournament-hub-card">
              <div className="flex items-center gap-3 mb-6">
                <Trophy className="h-6 w-6 text-accent" />
                <h2 className="font-barlow-condensed text-2xl font-bold text-primary tracking-tight">Tournament Hub</h2>
              </div>
              
              {tournaments.length === 0 ? (
                <p className="text-slate-500 text-sm">No tournaments available yet.</p>
              ) : (
                <div className="space-y-4">
                  {tournaments.map(tournament => (
                    <div key={tournament.id} className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-testid={`tournament-${tournament.id}`}>
                      <div className="p-5 flex flex-col sm:flex-row gap-4">
                        {/* Left: Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <h3 className="font-barlow-condensed text-lg font-bold text-primary">{tournament.title}</h3>
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary/20 text-secondary">
                                  {tournament.category}
                                </span>
                              </div>
                              <div className="space-y-1 text-sm text-slate-600">
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4 text-slate-400" />
                                  <span>{tournament.location || 'TBA'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-slate-400" />
                                  <span>{formatDate(tournament.start_date)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-slate-400" />
                                  <span>{formatTime(tournament.start_date)}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-2">Registration Deadline: {formatDate(tournament.registration_deadline)}</p>
                                {tournament.description && (
                                  <p className="text-sm text-slate-600 mt-2">{tournament.description}</p>
                                )}
                                {tournament.notes && (
                                  <div className="mt-3 p-2 bg-accent/10 border-l-2 border-accent rounded">
                                    <p className="text-xs font-semibold text-accent uppercase tracking-wide">Notes</p>
                                    <p className="text-sm text-slate-700">{tournament.notes}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="flex items-center gap-1 text-sm text-slate-600 mb-2 justify-end">
                                <Users className="h-4 w-4" />
                                <span>{tournament.registration_count}/{tournament.participant_limit}</span>
                              </div>
                              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-accent/20 text-accent">₱{Number(tournament.fee ?? 0).toLocaleString()}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => openTournamentRegistration(tournament)}
                            disabled={tournament.registration_count >= tournament.participant_limit}
                            className="bg-secondary hover:bg-secondary/90 text-white w-full sm:w-auto"
                            data-testid={`register-tournament-${tournament.id}`}
                          >
                            <Trophy className="h-4 w-4 mr-2" />
                            {tournament.registration_count >= tournament.participant_limit ? 'Tournament Full' : 'Register'}
                          </Button>
                          {attendees[tournament.id] && attendees[tournament.id].count > 0 && (
                            <div className="mt-4 pt-4 border-t border-slate-100">
                              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                Confirmed Players ({attendees[tournament.id].count})
                              </p>
                              <div className="flex flex-wrap gap-1.5" data-testid={`tournament-attendees-${tournament.id}`}>
                                {attendees[tournament.id].player_names.map((name, idx) => (
                                  <span key={`${tournament.id}-${name}-${idx}`} className="inline-block px-2 py-1 bg-accent/10 text-accent text-xs rounded-full font-medium">
                                    {name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right: Banner Thumbnail */}
                        {tournament.banner_path && (
                          <div className="sm:w-[150px] sm:shrink-0">
                            <button
                              type="button"
                              onClick={() => setLightboxBanner(`${process.env.REACT_APP_BACKEND_URL}/api/files/${tournament.banner_path}`)}
                              className="w-full block relative overflow-hidden bg-slate-100 cursor-zoom-in rounded-md group h-[180px] sm:h-[150px]"
                              data-testid={`tournament-banner-${tournament.id}`}
                              aria-label={`View ${tournament.title} banner`}
                            >
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${tournament.banner_path}`}
                                alt={tournament.title}
                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100">
                                <span className="text-white text-[10px] px-2 py-0.5 rounded bg-black/60 backdrop-blur-sm">Tap to expand</span>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Banner Lightbox */}
      {lightboxBanner && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxBanner(null)}
          data-testid="banner-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxBanner(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 backdrop-blur-sm transition-colors"
            data-testid="banner-lightbox-close"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxBanner}
            alt="Tournament banner"
            className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}


      {/* Balance Details Modal */}
      <Dialog open={showBalanceModal} onOpenChange={setShowBalanceModal}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-4 sm:px-6 pt-6 pb-4 border-b border-slate-200 flex-shrink-0">
            <DialogTitle className="font-barlow-condensed text-2xl">Balance Breakdown</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            <div className="bg-primary text-white p-4 rounded-lg">
              <p className="text-sm text-white/80">Total Unpaid Balance</p>
              <p className="font-barlow-condensed text-3xl font-bold" data-testid="modal-total-balance">₱{balanceDetails.total}</p>
            </div>
            {balanceDetails.items.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No unpaid items. You&apos;re all caught up! 🎉</p>
            ) : (
              <>
                {/* Mobile card list */}
                <div className="space-y-3 md:hidden">
                  {balanceDetails.items.map(item => (
                    <div key={item.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`balance-item-mobile-${item.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-primary">{item.player_name}</p>
                          <p className="text-xs text-slate-500 truncate">{item.event}</p>
                          <p className="text-xs text-slate-500">{formatDate(item.date)}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${item.type === 'training' ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'}`}>
                            {item.type}
                          </span>
                          <p className="font-semibold text-primary mt-1">₱{item.fee}</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-slate-100">
                        {item.has_receipt ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            ✓ Receipt uploaded
                          </span>
                        ) : (
                          <label className="cursor-pointer inline-block w-full">
                            <span className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-semibold bg-secondary text-white hover:bg-secondary/90 transition-colors" data-testid={`upload-receipt-btn-mobile-${item.id}`}>
                              Upload Receipt
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleUploadReceipt(item, e.target.files[0])}
                              data-testid={`upload-receipt-input-mobile-${item.id}`}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Player</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Receipt</TableHead>
                        <TableHead className="text-right">Fee</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {balanceDetails.items.map(item => (
                        <TableRow key={item.id} data-testid={`balance-item-${item.id}`}>
                          <TableCell className="font-medium">{item.player_name}</TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${item.type === 'training' ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'}`}>
                              {item.type}
                            </span>
                          </TableCell>
                          <TableCell>{item.event}</TableCell>
                          <TableCell className="text-sm">{formatDate(item.date)}</TableCell>
                          <TableCell>
                            {item.has_receipt ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                ✓ Uploaded
                              </span>
                            ) : (
                              <label className="cursor-pointer inline-block">
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-secondary text-white hover:bg-secondary/90 transition-colors" data-testid={`upload-receipt-btn-${item.id}`}>
                                  Upload
                                </span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleUploadReceipt(item, e.target.files[0])}
                                  data-testid={`upload-receipt-input-${item.id}`}
                                />
                              </label>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-semibold">₱{item.fee}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-barlow-condensed text-2xl">Past Activity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">All completed and paid events</p>
            {history.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No past activity yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Player</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(item => (
                    <TableRow key={item.id} data-testid={`history-item-${item.id}`}>
                      <TableCell className="font-medium">{item.player_name}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${item.type === 'training' ? 'bg-secondary/20 text-secondary' : 'bg-accent/20 text-accent'}`}>
                          {item.type}
                        </span>
                      </TableCell>
                      <TableCell>{item.event}</TableCell>
                      <TableCell className="text-sm">{formatDate(item.date)}</TableCell>
                      <TableCell>
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Paid</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold">₱{item.fee}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Check-In Dialog */}
      <Dialog open={showCheckIn} onOpenChange={setShowCheckIn}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Check-in for {selectedSession?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCheckIn} className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-primary mb-2">Payment Details</h3>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Fee:</span> ₱{selectedSession?.fee_type === 'standard' ? '100' : '250'} ({selectedSession?.fee_type === 'standard' ? 'Standard' : 'Special'})</p>
                <p className="text-xs text-slate-600 pt-1">Send payment via any active channel shown on your dashboard, then upload the receipt below.</p>
              </div>
            </div>
            <div>
              <Label>Select Player</Label>
              <Select value={checkInData.player_id} onValueChange={(value) => setCheckInData({ ...checkInData, player_id: value })} required>
                <SelectTrigger data-testid="check-in-player-select"><SelectValue placeholder="Choose a player" /></SelectTrigger>
                <SelectContent>
                  {players.map(player => (
                    <SelectItem key={player.id} value={player.id}>{player.name}{player.category ? ` (${player.category})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Upload GCash Receipt (Optional)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setCheckInData({ ...checkInData, file: e.target.files[0] })} data-testid="check-in-receipt-input" className="mt-1" />
              <p className="text-xs text-slate-500 mt-1">Upload receipt after making payment via GCash</p>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-accent hover:bg-accent/90" data-testid="check-in-submit-button">
              {loading ? 'Checking in...' : 'Confirm Check-in'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tournament Registration Dialog */}
      <Dialog open={showTournamentReg} onOpenChange={setShowTournamentReg}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Register for {selectedTournament?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTournamentRegistration} className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-primary mb-2">Tournament Details</h3>
              <div className="space-y-1 text-sm">
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {selectedTournament?.location || 'TBA'}</p>
                <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-400" /> {formatDate(selectedTournament?.start_date)}</p>
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> {formatTime(selectedTournament?.start_date)}</p>
                <p><span className="font-medium">Category:</span> {selectedTournament?.category}</p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-primary mb-2">Payment Info</h3>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Registration Fee:</span> ₱{Number(selectedTournament?.fee ?? 0).toLocaleString()}</p>
                <p className="text-xs text-slate-600 pt-1">Send payment via any active channel shown on your dashboard, then upload the receipt below.</p>
              </div>
            </div>
            <div>
              <Label>Select Player</Label>
              <Select value={tournamentRegData.player_id} onValueChange={(value) => setTournamentRegData({ ...tournamentRegData, player_id: value })} required>
                <SelectTrigger data-testid="tournament-player-select"><SelectValue placeholder="Choose a player" /></SelectTrigger>
                <SelectContent>
                  {players.map(player => (
                    <SelectItem key={player.id} value={player.id}>{player.name}{player.category ? ` (${player.category})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Upload GCash Receipt (Optional)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setTournamentRegData({ ...tournamentRegData, file: e.target.files[0] })} data-testid="tournament-receipt-input" className="mt-1" />
              <p className="text-xs text-slate-500 mt-1">You can upload later from the Balance Breakdown</p>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary text-white hover:bg-primary/90" data-testid="tournament-submit-button">
              {loading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
